import React, { useState, useEffect, useRef } from 'react';
import { theme } from '../theme';
import { Button } from './ui';
import Icons from './Icons';

// ---------------------------------------------------------------------------
// Bind Parameter Modal
// Shows a labelled input for each :param_name found in the query.
// Remembers previously entered values via the `initialValues` prop.
// ---------------------------------------------------------------------------
export default function BindParamModal({ params, initialValues = {}, onRun, onClose }) {
  const [values, setValues] = useState(() => {
    const init = {};
    for (const p of params) {
      init[p] = initialValues[p] ?? '';
    }
    return init;
  });

  // Focus the first input on mount
  const firstRef = useRef(null);
  useEffect(() => {
    firstRef.current?.focus();
  }, []);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onRun(values);
    }
    if (e.key === 'Escape') {
      onClose();
    }
  };

  const allFilled = params.every(p => values[p] !== '');

  return (
    <div
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(0,0,0,0.6)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: theme.surface,
          borderRadius: '12px',
          padding: '24px',
          width: '420px',
          maxWidth: '90vw',
          border: `1px solid ${theme.border}`,
          boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
        }}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={handleKeyDown}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '18px' }}>
          <Icons.Zap style={{ width: 16, height: 16, color: theme.accent }} />
          <h3 style={{ margin: 0, fontSize: '15px', fontWeight: 600, color: theme.text }}>
            Query Parameters
          </h3>
          <button
            onClick={onClose}
            style={{
              marginLeft: 'auto',
              background: 'none', border: 'none', cursor: 'pointer',
              color: theme.textMuted, padding: '2px',
            }}
          >
            <Icons.X style={{ width: 14, height: 14 }} />
          </button>
        </div>

        <p style={{ fontSize: '12px', color: theme.textMuted, margin: '0 0 16px' }}>
          Enter values for the bind parameters. Dates and text are quoted automatically — numbers and SQL expressions (e.g. <code style={{ fontFamily: 'monospace' }}>SYSDATE</code>) are used as-is.
        </p>

        {/* Parameter inputs */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {params.map((param, idx) => (
            <div key={param}>
              <label style={{
                display: 'block',
                fontSize: '12px',
                color: theme.textMuted,
                marginBottom: '4px',
                fontFamily: "'SF Mono','Fira Code',Consolas,monospace",
              }}>
                :{param}
              </label>
              <input
                ref={idx === 0 ? firstRef : undefined}
                value={values[param]}
                onChange={(e) => setValues(v => ({ ...v, [param]: e.target.value }))}
                placeholder={`Value for :${param}`}
                style={{
                  width: '100%',
                  boxSizing: 'border-box',
                  background: theme.bg,
                  border: `1px solid ${theme.border}`,
                  borderRadius: '6px',
                  padding: '8px 10px',
                  color: theme.text,
                  fontSize: '13px',
                  fontFamily: "'SF Mono','Fira Code',Consolas,monospace",
                  outline: 'none',
                }}
                onFocus={(e) => { e.target.style.borderColor = theme.accent; }}
                onBlur={(e) => { e.target.style.borderColor = theme.border; }}
              />
            </div>
          ))}
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '20px' }}>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button
            onClick={() => onRun(values)}
            disabled={!allFilled}
            icon={<Icons.Play style={{ width: 13, height: 13 }} />}
          >
            Run Query
          </Button>
        </div>

        <p style={{ fontSize: '11px', color: theme.textMuted, margin: '10px 0 0', textAlign: 'right' }}>
          Press Enter to run · Esc to cancel
        </p>
      </div>
    </div>
  );
}
